import { useState, useEffect } from '@wordpress/element';
import { convertStateCode } from '../../util';

const LocationGate = ({
    locationGateConfig,
    renderHtml,
    retailers = {},
    styleOptions,
    handleLocationSelect,
    nonHeadlessRetailers = [], // Default to empty array
    customRetailersOrder = []
}) => {
    const [expandedStates, setExpandedStates] = useState({});
    const [orderedLocations, setOrderedLocations] = useState([]);

    // Helper function to get a unique identifier for a retailer
    const getRetailerId = (retailer) => retailer.id || `non-headless-${retailer.location_name}`;

    // Helper function to get retailer name consistently for display
    const getRetailerName = (retailer) => (
        retailer.niceName ||
        retailer.retailerName ||
        retailer.location_name ||
        getRetailerId(retailer)
    );

    // Helper function to get the matching name for custom order comparison
    const getMatchingName = (retailer) => {
        if (retailer.isNonHeadless) {
            return retailer.location_name;
        }
        return retailer.niceName;
    };

    useEffect(() => {
        if (locationGateConfig.locationExpandedStates) {
            const allStates = Object.keys(groupRetailersByState());
            const initialExpandedStates = allStates.reduce((acc, state) => {
                acc[state] = true;
                return acc;
            }, {});
            setExpandedStates(initialExpandedStates);
        }
    }, [locationGateConfig.locationExpandedStates, retailers, nonHeadlessRetailers]);

    useEffect(() => {
        // Create a map of all locations for easy lookup
        const allLocations = new Map();

        // Add headless retailers
        Object.values(retailers).forEach(retailer => {
            const retailerId = getRetailerId(retailer);
            allLocations.set(retailerId, {
                ...retailer,
                displayName: getRetailerName(retailer),
                isNonHeadless: false
            });
        });

        // Add non-headless retailers - with null check
        if (Array.isArray(nonHeadlessRetailers)) {
            nonHeadlessRetailers.forEach(retailer => {
                const retailerId = getRetailerId(retailer);
                allLocations.set(retailerId, {
                    ...retailer,
                    displayName: getRetailerName(retailer),
                    isNonHeadless: true
                });
            });
        }

        // Handle custom ordering if provided
        if (customRetailersOrder?.length) {
            // Create a map of custom order configurations for lookup
            const orderMap = new Map(
                customRetailersOrder.map((order, index) => [
                    order.custom_retailers_order_name,
                    { ...order, order: index }
                ])
            );

            // Debug each retailer matching
            Array.from(allLocations.values()).forEach(retailer => {
                const matchingName = getMatchingName(retailer);
                const hasMatch = orderMap.has(matchingName);
            });

            // Check if we have a complete match
            const allRetailersHaveOrder = Array.from(allLocations.values()).every(retailer => {
                const matchingName = getMatchingName(retailer);
                return orderMap.has(matchingName);
            });

            if (allRetailersHaveOrder) {
                const orderedRetailers = Array.from(allLocations.values()).sort((a, b) => {
                    const matchNameA = getMatchingName(a);
                    const matchNameB = getMatchingName(b);
                    const orderA = orderMap.get(matchNameA)?.order ?? 0;
                    const orderB = orderMap.get(matchNameB)?.order ?? 0;
                    
                    return orderA - orderB;
                });
        
                setOrderedLocations(orderedRetailers);
            } else {
                setOrderedLocations(Array.from(allLocations.values()));
            }
        } else {
            // No custom order, use default order
            setOrderedLocations(Array.from(allLocations.values()));
        }
    }, [retailers, nonHeadlessRetailers, customRetailersOrder]);

    function groupRetailersByState() {
        const retailersByState = {};

        orderedLocations.forEach((retailer) => {
            const state = retailer.isNonHeadless ? retailer.state : retailer.retailerState;
            if (state) {
                if (!retailersByState[state]) {
                    retailersByState[state] = [];
                }
                retailersByState[state].push(retailer);
            }
        });

        return retailersByState;
    }

    function renderRetailerList(retailers) {
        return (
            <div className='headless-hlm-locations'>
                {retailers.map((retailer) => {
                    const retailerId = getRetailerId(retailer);
                    const retailerName = getRetailerName(retailer);
                    const retailerImage = retailer.locationImage;
                    const isNonHeadless = retailer.isNonHeadless;

                    let addressDisplay = isNonHeadless
                        ? `${retailer.address || ''}, ${retailer.city || ''}, ${retailer.state || ''} ${retailer.zip || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '')
                        : retailer.address || '';

                    return (
                        <div
                            className={`headless-hlm-location ${retailerImage ? 'has-location-image' : ''}`}
                            data-retailer-id={retailerId}
                            key={retailerId}
                            onClick={() => handleLocationSelect(retailer)}
                        >
                            {retailerImage && (
                                <div
                                    className="headless-hlm-location-image"
                                    style={{ backgroundImage: `url(${retailerImage})` }}
                                ></div>
                            )}
                            <div className="headless-hlm-location-info-container">
                                <div className='headless-hlm-location-info'>
                                    <div className='headless-hlm-location-name'>
                                        {retailerName}
                                    </div>
                                    <div className='headless-hlm-location-address'>
                                        {addressDisplay}
                                    </div>
                                </div>
                                <div className='headless-hlm-location-icon'>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                        <path d="M305 239c9.4 9.4 9.4 24.6 0 33.9L113 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L79 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L305 239z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    const isStateList = locationGateConfig.locationListType === 'state';
    const retailersByState = isStateList ? groupRetailersByState() : null;

    function toggleState(state) {
        setExpandedStates(prevStates => ({
            ...prevStates,
            [state]: !prevStates[state]
        }));
    }

    return (
        <div className='headless-location-gate'>
            <div className='headless-hlm-title'>
                {locationGateConfig.locationGateContentArea ? (
                    <div
                        dangerouslySetInnerHTML={renderHtml(
                            locationGateConfig.locationGateContentArea
                        )}
                    />
                ) : (
                    'Select your location'
                )}
            </div>
            <div className='headless-location-gate-locations-wrapper'>
                {isStateList
                    ? Object.entries(retailersByState || {}).map(([state, retailers]) => (
                        <div
                            className={`headless-location-gate-state ${expandedStates[state] ? 'location-state-expanded' : ''}`}
                            onClick={() => toggleState(state)}
                            key={state}
                            data-state={state}
                        >
                            <div className='headless-location-gate-state-name'>
                                {convertStateCode(state)}
                            </div>
                            {expandedStates[state] && renderRetailerList(retailers)}
                        </div>
                    ))
                    : renderRetailerList(orderedLocations)}
            </div>
        </div>
    );
};

export default LocationGate;
